// frontend/src/store/savedViewsStore.ts
// Brighella v2 (ADR-013 P8 / ADR-014 D3) — saved views: any filter+view-mode
// combo on a hub is nameable, saveable, shareable inside the tenant, and
// pinnable to the sidebar.
//
// This is a server-backed Zustand store — it is NOT persisted to localStorage.
// All state lives in Postgres via /api/saved-views; the store is just a cache
// + the loading/error envelope the UI renders against.

import { create } from 'zustand'
import api from '../utils/api.js'
import { withIfMatch, isVersionConflict } from '../utils/concurrency.js'

export type SavedViewVisibility = 'private' | 'tenant'

export interface SavedView {
  id: number
  owner_id: number
  owner_name: string
  surface: string
  name: string
  visibility: SavedViewVisibility
  /** Opaque serialized hub state (filters + view-mode + search). */
  state: unknown
  pinned: boolean
  pin_order: number | null
  /** True when the caller owns the view (editable); false for tenant-shared. */
  is_own: boolean
  /**
   * Optimistic-concurrency version (finding M3). Incremented by the backend on
   * every successful update; echoed back to the server in the `If-Match`
   * header of the next PUT so a stale write is rejected with 409.
   */
  version: number
  created_at: string
  updated_at: string
}

export interface CreateViewInput {
  surface: string
  name: string
  visibility?: SavedViewVisibility
  state: unknown
}

export interface UpdateViewPatch {
  name?: string
  visibility?: SavedViewVisibility
  state?: unknown
}

export interface SavedViewsState {
  /** Views keyed by surface — own views + tenant-shared views in that surface. */
  views: Record<string, SavedView[]>
  /** The caller's own pinned views across all surfaces, ordered by pin_order. */
  pinned: SavedView[]
  /** True while a per-surface load is in flight. */
  loading: boolean
  /** True while loadPinned() is in flight. */
  pinnedLoading: boolean
  /** Last error message (network / 403 / etc.) — generic surface-level errors. */
  error: string | null
  /** Last create/update error keyed for the UI — esp. 409 duplicate name. */
  saveError: string | null
  /**
   * True when the last updateView() was rejected with a 409 VERSION_CONFLICT —
   * another user saved the same view first (finding M3). The UI must show the
   * `errors.versionConflict` message and prompt a reload; the store cannot
   * translate, so it flags the condition and the component renders the i18n string.
   */
  saveConflict: boolean

  loadViews: (surface: string) => Promise<void>
  loadPinned: () => Promise<void>
  createView: (input: CreateViewInput) => Promise<SavedView | null>
  updateView: (id: number, patch: UpdateViewPatch) => Promise<SavedView | null>
  deleteView: (id: number) => Promise<boolean>
  setPin: (id: number, pinned: boolean, pinOrder?: number) => Promise<boolean>
  /** Clear the inline save error (call when the user dismisses / retypes). */
  clearSaveError: () => void
}

/** Extract a human-readable message from an api.js error object. */
function errMessage(err: unknown): string {
  const e = err as { response?: { data?: { error?: string; message?: string } }; message?: string }
  return (
    e?.response?.data?.error ||
    e?.response?.data?.message ||
    e?.message ||
    'Request failed'
  )
}

function statusOf(err: unknown): number | undefined {
  return (err as { response?: { status?: number } })?.response?.status
}

export const useSavedViewsStore = create<SavedViewsState>((set, get) => ({
  views: {},
  pinned: [],
  loading: false,
  pinnedLoading: false,
  error: null,
  saveError: null,
  saveConflict: false,

  loadViews: async (surface) => {
    set({ loading: true, error: null })
    try {
      const res = await api.get('/saved-views', { params: { surface } })
      const rows: SavedView[] = Array.isArray(res.data) ? res.data : []
      set((s) => ({
        views: { ...s.views, [surface]: rows },
        loading: false,
      }))
    } catch (err) {
      set({ loading: false, error: errMessage(err) })
    }
  },

  loadPinned: async () => {
    set({ pinnedLoading: true, error: null })
    try {
      const res = await api.get('/saved-views/pinned')
      const rows: SavedView[] = Array.isArray(res.data) ? res.data : []
      set({ pinned: rows, pinnedLoading: false })
    } catch (err) {
      set({ pinnedLoading: false, error: errMessage(err) })
    }
  },

  createView: async ({ surface, name, visibility = 'private', state }) => {
    set({ saveError: null })
    try {
      const res = await api.post<SavedView>('/saved-views', { surface, name, visibility, state })
      const row: SavedView = res.data
      set((s) => ({
        views: {
          ...s.views,
          [surface]: [...(s.views[surface] || []), row],
        },
      }))
      return row
    } catch (err) {
      // 409 → duplicate (owner, surface, name). Surface it for inline display.
      const status = statusOf(err)
      set({
        saveError:
          status === 409
            ? errMessage(err) || 'A view with that name already exists.'
            : errMessage(err),
      })
      return null
    }
  },

  updateView: async (id, patch) => {
    set({ saveError: null, saveConflict: false })
    // Optimistic concurrency (finding M3): echo the version of the cached row
    // (last read from GET /saved-views) back to the server in the `If-Match`
    // header so a stale write is rejected with 409 VERSION_CONFLICT.
    let knownVersion: number | undefined
    for (const list of Object.values(get().views)) {
      const found = list.find((v) => v.id === id)
      if (found) { knownVersion = found.version; break }
    }
    if (knownVersion === undefined) {
      const pinnedHit = get().pinned.find((v) => v.id === id)
      if (pinnedHit) knownVersion = pinnedHit.version
    }
    try {
      const res = await api.put<SavedView>(`/saved-views/${id}`, patch, withIfMatch(knownVersion))
      const row: SavedView = res.data
      // The 200 response carries the incremented `version`; spreading `row`
      // into the cached views updates it so a second consecutive edit works
      // without a reload.
      set((s) => {
        const nextViews: Record<string, SavedView[]> = {}
        for (const [surface, list] of Object.entries(s.views)) {
          nextViews[surface] = list.map((v) => (v.id === id ? { ...v, ...row } : v))
        }
        return {
          views: nextViews,
          pinned: s.pinned.map((v) => (v.id === id ? { ...v, ...row } : v)),
        }
      })
      return row
    } catch (err) {
      // 409 VERSION_CONFLICT — another user saved this view first. Distinct
      // from a 409 duplicate-name: flag it so the UI shows the
      // `errors.versionConflict` reload message instead of the name message.
      if (isVersionConflict(err)) {
        set({ saveConflict: true, saveError: null })
        return null
      }
      const status = statusOf(err)
      set({
        saveError:
          status === 409
            ? errMessage(err) || 'A view with that name already exists.'
            : errMessage(err),
      })
      return null
    }
  },

  deleteView: async (id) => {
    set({ error: null })
    try {
      await api.delete(`/saved-views/${id}`)
      set((s) => {
        const nextViews: Record<string, SavedView[]> = {}
        for (const [surface, list] of Object.entries(s.views)) {
          nextViews[surface] = list.filter((v) => v.id !== id)
        }
        return {
          views: nextViews,
          pinned: s.pinned.filter((v) => v.id !== id),
        }
      })
      return true
    } catch (err) {
      set({ error: errMessage(err) })
      return false
    }
  },

  setPin: async (id, pinned, pinOrder) => {
    set({ error: null })
    try {
      const body: { pinned: boolean; pin_order?: number } = { pinned }
      if (typeof pinOrder === 'number') body.pin_order = pinOrder
      const res = await api.put<SavedView | undefined>(`/saved-views/${id}/pin`, body)
      const row: SavedView | undefined = res.data
      set((s) => {
        const apply = (v: SavedView): SavedView =>
          v.id === id
            ? { ...v, ...(row || {}), pinned, pin_order: row?.pin_order ?? pinOrder ?? v.pin_order }
            : v
        const nextViews: Record<string, SavedView[]> = {}
        for (const [surface, list] of Object.entries(s.views)) {
          nextViews[surface] = list.map(apply)
        }
        return { views: nextViews }
      })
      // Re-sync the pinned list so the sidebar reflects the change.
      await get().loadPinned()
      return true
    } catch (err) {
      set({ error: errMessage(err) })
      return false
    }
  },

  clearSaveError: () => set({ saveError: null, saveConflict: false }),
}))

/** Reset hook for tests. Safe to call from beforeEach. */
export function _resetSavedViewsStoreForTests(): void {
  useSavedViewsStore.setState({
    views: {},
    pinned: [],
    loading: false,
    pinnedLoading: false,
    error: null,
    saveError: null,
    saveConflict: false,
  })
}
